import { Skeleton } from '@shared/ui/skeleton';
import { DelayedFallback } from '@shared/ui/delayed-fallback';

import styles from './preview.module.css';

const TOC_SKELETON_ITEMS = 10;

// A cold-cache preview often resolves in well under 300ms -- rendering a
// skeleton (or the old spinner) the instant loading starts meant a visible
// flash on screen for a frame or two before the real content replaced it,
// which read as more jarring than showing nothing at all would have
// (user-reported 2026-08-27). Withholding the skeleton for this long means
// a fast load never shows *any* loading UI -- blank/white the whole time --
// and only a load that's genuinely slow enough for a reader to notice gets
// the shimmer. Matches the `pendingMs`/`pendingMinMs` set on
// `previewViewRoute` in `route-catalog.tsx` for this page's *other* loading
// window (see `DelayedPreviewSkeleton`'s own doc comment for why that one
// needs a second, separate delay mechanism).
const SKELETON_DELAY_MS = 300;

/**
 * The skeleton itself -- shaped like the real `Cover`/`Toc` it stands in
 * for, using the same CSS classes so it lays out identically at every
 * breakpoint. Used directly (undelayed) as this page's route-level
 * `pendingComponent` (`route-loading.tsx`) -- that window's own delay is
 * TanStack Router's native `pendingMs`/`pendingMinMs` (set on
 * `previewViewRoute`), which withholds mounting this component at all
 * until the route has genuinely been pending for that long, so there's
 * nothing left for this component itself to delay.
 */
export function PreviewSkeleton() {
	return <CoverSkeleton />;
}

/**
 * `Content()`'s own `isLoading` (a `useGetIssue`/react-query fetch, not a
 * route transition) is invisible to the router entirely -- `pendingMs`
 * only ever fires for the route's own pending state, so this window needs
 * its own delay, applied here instead.
 */
export function DelayedPreviewSkeleton() {
	return <DelayedFallback delay={SKELETON_DELAY_MS} fallback={<CoverSkeleton />} />;
}

function CoverSkeleton() {
	return (
		<div className={styles['cover']} aria-busy="true">
			<section>
				<div className={styles['image']}>
					<Skeleton
						style={{ aspectRatio: '500 / 667', width: '100%' }}
						className="max-w-[500px] min-w-[300px] mx-auto bg-gray-300"
					/>
				</div>
				<div className={styles['title']}>
					<Skeleton className="h-6 w-2/3 mx-auto bg-gray-300" />
					<Skeleton className="h-4 w-1/2 mx-auto mt-2 bg-gray-300" />
				</div>
			</section>
			<section>
				<ul className={styles['toc']}>
					{Array.from({ length: TOC_SKELETON_ITEMS }).map((_, i) => (
						<li key={i}>
							<Skeleton className="h-5 w-3/4 mx-auto bg-gray-300" />
						</li>
					))}
				</ul>
			</section>
		</div>
	);
}
